// src/components/LearnerBalances.js
// Learner Balances / Debtors report: one row per learner showing Total
// Invoiced, Total Paid and Balance, filterable by admission no, academic year,
// date range, class and stage. Year/date scope the totals to that period; with
// no year/date filter the balance is lifetime. Click a row to open that
// learner's full statement (ledger).
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Alert, Grid, CircularProgress, IconButton, Tooltip, Button,
  TextField, MenuItem, FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LearnerBalances = () => {
  const navigate = useNavigate();
  const company = useCompany();

  // Filter option lists
  const [years, setYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stages, setStages] = useState([]);

  // Filter values
  const [search, setSearch] = useState(''); // client-side: filters loaded rows by name or adm no
  const [academicYear, setAcademicYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [classCode, setClassCode] = useState('');
  const [stage, setStage] = useState('');
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);

  // Results
  const [report, setReport] = useState(null); // { rows, totals, count }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load filter dropdowns once
  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => setYears(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/api/academics/classes`, { headers: hdrs() })
      .then(r => setClasses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/api/stages`, { headers: hdrs() })
      .then(r => setStages(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    setError('');
    setReport(null);
    try {
      const params = {};
      if (academicYear) params.academicYear = academicYear;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (classCode) params.classCode = classCode;
      if (stage) params.stage = stage;

      const res = await axios.get(`${API}/api/finance/learners/balances-report`, {
        headers: hdrs(),
        params,
      });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load balances report');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setAcademicYear('');
    setStartDate('');
    setEndDate('');
    setClassCode('');
    setStage('');
    setOnlyOutstanding(false);
  };

  // Rows to display: filtered by the name/adm-no search, optionally hiding
  // fully-settled / credit accounts, sorted by balance (biggest debtor first).
  const displayRows = () => {
    if (!report) return [];
    let rows = [...report.rows];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        r => r.name.toLowerCase().includes(q) || String(r.admNo).toLowerCase().includes(q)
      );
    }
    if (onlyOutstanding) rows = rows.filter(r => r.balance > 0.005);
    rows.sort((a, b) => b.balance - a.balance);
    return rows;
  };

  const rows = displayRows();

  // Totals reflect what's shown (so they stay consistent with the "only
  // outstanding" toggle rather than always echoing the server grand total).
  const totals = rows.reduce((acc, r) => {
    acc.totalInvoiced += r.totalInvoiced;
    acc.totalPaid += r.totalPaid;
    acc.balance += r.balance;
    return acc;
  }, { totalInvoiced: 0, totalPaid: 0, balance: 0 });

  const periodLabel = () => {
    const bits = [];
    if (academicYear) bits.push(`Year ${academicYear}`);
    if (startDate || endDate) bits.push(`${startDate || 'Start'} to ${endDate || 'End'}`);
    if (classCode) bits.push(`Class ${classCode}`);
    if (stage) bits.push(`Stage ${stage}`);
    return bits.length ? bits.join(' · ') : 'All time · All learners';
  };

  // Load the company logo as a data URL for the PDF. Fetches the image as a blob
  // (the /logo/download endpoint requires the auth token, and a blob avoids the
  // canvas-tainting an <img> hits cross-origin) and reads it with FileReader.
  // Degrades to null on any failure so the export still succeeds without it.
  const loadLogoDataUrl = async () => {
    try {
      let blob;
      if (company?.logoData) {
        const res = await axios.get(`${API}/api/company/logo/download`, {
          headers: hdrs(),
          responseType: 'blob',
        });
        blob = res.data;
      } else if (company?.logoUrl) {
        const res = await axios.get(company.logoUrl, { responseType: 'blob' });
        blob = res.data;
      } else {
        return null;
      }

      const type = blob.type || '';
      if (type.includes('svg')) return null; // jsPDF addImage can't embed SVG
      const format = type.includes('jpeg') || type.includes('jpg') ? 'JPEG' : 'PNG';

      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });

      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
        img.onerror = () => resolve({ w: 1, h: 1 });
        img.src = dataUrl;
      });

      return { dataUrl, format, w: dims.w, h: dims.h };
    } catch {
      return null;
    }
  };

  const exportPDF = async () => {
    if (!rows.length) return;
    const doc = new jsPDF();

    let y = 16;
    const logo = await loadLogoDataUrl();
    if (logo) {
      const h = 16;
      const w = Math.min(40, (logo.w / logo.h) * h);
      doc.addImage(logo.dataUrl, logo.format, 14, y - 4, w, h);
    }
    if (company?.name) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text(company.name, 105, y, { align: 'center' });
      y += 6;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (company?.kraPin) { doc.text(`Revenue Authority No.: ${company.kraPin}`, 105, y, { align: 'center' }); y += 4; }
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.line(14, y, 196, y);
    y += 8;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('LEARNER BALANCES', 105, y, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    y += 6;
    doc.setFontSize(9);
    doc.text(periodLabel(), 105, y, { align: 'center' });
    y += 4;
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, y, { align: 'center' });
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [['Adm No', 'Name', 'Class', 'Stage', 'Invoiced', 'Paid', 'Balance']],
      body: rows.map(r => [
        r.admNo, r.name, r.classCode || '-', r.stage || '-',
        r.totalInvoiced.toFixed(2), r.totalPaid.toFixed(2), r.balance.toFixed(2),
      ]),
      foot: [['', '', '', 'TOTALS', totals.totalInvoiced.toFixed(2), totals.totalPaid.toFixed(2), totals.balance.toFixed(2)]],
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [220, 220, 220], fontStyle: 'bold', halign: 'right' },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold' },
      },
    });

    doc.save('learner_balances.pdf');
  };

  const exportCSV = () => {
    if (!rows.length) return;
    const out = [
      [company?.name || 'LEARNER BALANCES'],
      ['LEARNER BALANCES'],
      [periodLabel()],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Adm No', 'Name', 'Class', 'Stage', 'Total Invoiced', 'Total Paid', 'Balance'],
      ...rows.map(r => [
        r.admNo, r.name, r.classCode || '', r.stage || '',
        r.totalInvoiced, r.totalPaid, r.balance,
      ]),
      [],
      ['', '', '', 'TOTALS', totals.totalInvoiced, totals.totalPaid, totals.balance],
    ];
    const csv = out
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `learner_balances_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={6}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={10}>
              <Typography variant="h5" component="h1" color="primary" fontWeight="bold">
                Learner Balances / Debtors
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'right' }}>
              <Tooltip title="Back to Dashboard">
                <IconButton color="primary" onClick={() => navigate('/finance')}>
                  <HomeIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          {/* ── Filters ── */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Search (name or adm no)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type a name or admission number…"
                fullWidth
                size="small"
                helperText={report ? 'Filters the list below as you type' : 'Generate first, then filter'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select label="Academic Year" value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)} fullWidth size="small"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Stage" value={stage}
                onChange={(e) => { setStage(e.target.value); setClassCode(''); }} fullWidth size="small"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Class" value={classCode}
                onChange={(e) => setClassCode(e.target.value)} fullWidth size="small"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {classes
                  .filter(c => !stage || c.stageCode === stage)
                  .map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} fullWidth size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date" type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate || undefined }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained" onClick={generate} disabled={loading}
                startIcon={loading ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                {loading ? 'Loading…' : 'Generate'}
              </Button>
              <Button variant="text" onClick={clearFilters} disabled={loading}>Clear</Button>
              <FormControlLabel
                control={<Checkbox checked={onlyOutstanding} onChange={(e) => setOnlyOutstanding(e.target.checked)} />}
                label="Only with balance"
              />
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {report && (
            <>
              <CompanyHeader title="Learner Balances" subtitle={periodLabel()} />

              {/* ── Summary tiles ── */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Learners</Typography>
                    <Typography variant="h6" fontWeight="bold">{rows.length}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(totals.totalInvoiced)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(totals.totalPaid)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ p: 2, bgcolor: totals.balance >= 0 ? 'error.light' : 'success.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {totals.balance >= 0 ? 'Total Outstanding' : 'Total in Credit'}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">{sym()} {fmt(Math.abs(totals.balance))}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* ── Export buttons ── */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Button variant="outlined" onClick={exportPDF} fullWidth startIcon={<PictureAsPdfIcon />} disabled={!rows.length}>
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button variant="outlined" color="success" onClick={exportCSV} fullWidth startIcon={<DescriptionIcon />} disabled={!rows.length}>
                    Export Excel
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* ── Table ── */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adm No</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Class</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stage</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total Invoiced</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total Paid</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No learners match the selected filters</TableCell>
                      </TableRow>
                    ) : (
                      rows.map((r) => (
                        <TableRow
                          key={r.admNo}
                          hover
                          onClick={() => navigate(`/ledger/${r.admNo}`)}
                          sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}
                        >
                          <TableCell>{r.admNo}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell>{r.classCode || '-'}</TableCell>
                          <TableCell>{r.stage || '-'}</TableCell>
                          <TableCell align="right">{fmt(r.totalInvoiced)}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(r.totalPaid)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 'bold', color: r.balance > 0.005 ? 'error.main' : r.balance < -0.005 ? 'success.main' : 'text.secondary' }}
                          >
                            {fmt(r.balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {rows.length > 0 && (
                      <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>TOTALS:</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(totals.totalInvoiced)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{fmt(totals.totalPaid)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: totals.balance >= 0 ? 'error.main' : 'success.main' }}>
                          {fmt(totals.balance)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Tip: click a learner to open their full statement.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LearnerBalances;
